import Button from '@atlaskit/button/standard-button';
import { Checkbox } from '@atlaskit/checkbox';
import ModalDialog, { ModalBody, ModalHeader } from '@atlaskit/modal-dialog';
import { token } from '@atlaskit/tokens';
import { BodySmall, H5 } from '@atlassian/mpac-primitives';
import { styled } from '@compiled/react';
import React, { useEffect, useState } from 'react';

const Container = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.300', '24px'),
  paddingBlock: token('space.300', '24px'),
});

const FieldsContainerDiv = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.200', '16px'),
});

const FieldsDiv = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.300', '24px'),
});

const FieldSet = styled.fieldset({
  border: 'none',
  padding: 0,
  margin: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.300', '24px'),
});

const QuestionSection = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.200', '16px'),
});

const OptionsStack = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.100', '8px'),
});

const ButtonsContainer = styled.div({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: token('space.100', '8px'),
  marginTop: token('space.200', '16px'),
});

interface RequestBundleDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: any) => Promise<void>;
}

const discoveryOptions = [
  { id: 'home_page', label: 'On the Atlassian Marketplace home page' },
  { id: 'search_results', label: "In search results when I'm looking for apps" },
  { id: 'recommendations', label: 'Through recommendations based on how I use Jira/Confluence' },
  { id: 'documentation', label: 'In blog posts, documentation, or solution articles' },
];

const BundleForm: React.FC<{
  selectedDiscovery: string[];
  onDiscoveryChange: (id: string) => void;
  onClose: () => void;
  onSubmit: () => void;
  canSubmit: boolean;
}> = ({ selectedDiscovery, onDiscoveryChange, onClose, onSubmit, canSubmit }) => (
  <Container>
    <FieldsContainerDiv>
      <FieldsDiv>
        <FieldSet>
          <QuestionSection>
            <BodySmall>
              Where would you want to discover app bundles? (Select all that apply)
            </BodySmall>
            <OptionsStack>
              {discoveryOptions.map((option) => (
                <Checkbox
                  key={option.id}
                  label={<BodySmall>{option.label}</BodySmall>}
                  isChecked={selectedDiscovery.includes(option.id)}
                  onChange={() => onDiscoveryChange(option.id)}
                />
              ))}
            </OptionsStack>
          </QuestionSection>
        </FieldSet>

        <ButtonsContainer>
          <Button appearance="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button appearance="primary" onClick={onSubmit} isDisabled={!canSubmit}>
            Submit response
          </Button>
        </ButtonsContainer>
      </FieldsDiv>
    </FieldsContainerDiv>
  </Container>
);

export const RequestBundleDialog: React.FC<RequestBundleDialogProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [selectedDiscovery, setSelectedDiscovery] = useState<string[]>([]);

  const resetSelections = () => {
    setSelectedDiscovery([]);
  };

  useEffect(() => {
    if (!isOpen) {
      resetSelections();
    }
  }, [isOpen]);

  const handleClose = () => {
    resetSelections();
    onClose();
  };

  const handleDiscoveryChange = (optionId: string) => {
    setSelectedDiscovery((prev) => {
      if (prev.includes(optionId)) {
        return prev.filter((id) => id !== optionId);
      }
      return [...prev, optionId];
    });
  };

  const handleSubmit = async () => {
    try {
      await onSubmit({
        discoveryPreferences: selectedDiscovery,
      });
      handleClose();
    } catch (error) {
      console.log('Error submitting request bundle form', error);
      // Let the parent handle the error
      throw error;
    }
  };

  const canSubmit = Boolean(selectedDiscovery.length > 0);

  return isOpen ? (
    <ModalDialog onClose={handleClose}>
      <ModalHeader>
        <FieldsContainerDiv>
          <H5>Thanks for your interest in app bundles!</H5>
        </FieldsContainerDiv>
      </ModalHeader>
      <ModalBody>
        <BodySmall data-testid="app-bundle-request-description">
          App bundles are currently a work in progress, and we’re gathering interest to help us
          offer bundles in the future. In the meantime, we’d appreciate your help in answering the
          following question.
        </BodySmall>
        <BundleForm
          selectedDiscovery={selectedDiscovery}
          onDiscoveryChange={handleDiscoveryChange}
          onClose={handleClose}
          onSubmit={handleSubmit}
          canSubmit={canSubmit}
        />
      </ModalBody>
    </ModalDialog>
  ) : null;
};
